import { useCallback, useEffect, useState } from 'react';

import { puCancellationEnabled } from '../lib/cancellation-api';
import { type ICancellationSnapshot, readSnapshot, subscribeCancellationChanged } from '../lib/cancellation-store';

/**
 * Whether the agent's own polling unit is locked because INEC cancelled it.
 *
 * <p>The rule, in one place for every surface (assignment card, upload panel, Pulse, result
 * video): the unit is LOCKED once the party's ledger holds an Active cancellation for it —
 * reviewed or not. A report that only exists on this phone (`localOnly`) does not lock yet: the
 * agent keeps the warning until it reaches the party, so a report filed by mistake offline can
 * never strand a real result sheet. Reinstated or Withdrawn unlocks.</p>
 *
 * <p>Device-only: reads the store the overview's `useCancellationStatus` keeps fresh, so mounting
 * this anywhere adds no requests.</p>
 */
export interface IUnitLock {
	/** Uploads, resubmits and Pulse captures are refused until the party reinstates the unit. */
	locked: boolean;
	/** Reported on this phone but not yet received by the party — warn only. */
	pendingSync: boolean;
	reason?: ICancellationSnapshot['reason'];
	cancelledAt?: string;
	/** A party admin has confirmed the cancellation. */
	reviewed: boolean;
	/** Set once a unit this device saw locked comes back Reinstated; cleared by `acknowledge`. */
	reinstated: { at?: string | null; reason?: string | null } | null;
	acknowledgeReinstated: () => void;
}

const SEEN_KEY = (electionId: number, puCode: string) => `ielect-pu-lock-seen:${electionId}:${puCode}`;

const readSeen = (electionId: number, puCode: string): string | null => {
	try {
		return localStorage.getItem(SEEN_KEY(electionId, puCode));
	} catch {
		return null;
	}
};

const writeSeen = (electionId: number, puCode: string, rowId: number | null) => {
	try {
		if (rowId == null) localStorage.removeItem(SEEN_KEY(electionId, puCode));
		else localStorage.setItem(SEEN_KEY(electionId, puCode), String(rowId));
	} catch {
		// Storage blocked — the reinstatement banner then shows for this session only.
	}
};

const INACTIVE: ICancellationSnapshot = { active: false, localOnly: false };

export const isLockedSnapshot = (snapshot: ICancellationSnapshot): boolean => snapshot.active && !snapshot.localOnly;

/**
 * The same rule without a hook — for a submit handler that must re-check at the moment of
 * sending (the panel may have opened before the cancellation synced).
 */
export const isUnitLocked = (electionId: number | undefined, puCode: string | undefined): boolean =>
	!!electionId && !!puCode && puCancellationEnabled() && isLockedSnapshot(readSnapshot(electionId, puCode));

export const useUnitLock = (electionId: number | undefined, puCode: string | undefined, enabled: boolean): IUnitLock => {
	const active = enabled && !!electionId && !!puCode && puCancellationEnabled();
	const [snapshot, setSnapshot] = useState<ICancellationSnapshot>(() => (active ? readSnapshot(electionId, puCode) : INACTIVE));
	const [seen, setSeen] = useState<string | null>(() => (active ? readSeen(electionId, puCode) : null));

	useEffect(() => {
		if (!active) {
			setSnapshot(INACTIVE);
			setSeen(null);
			return;
		}
		const read = () => setSnapshot(readSnapshot(electionId, puCode));
		read();
		setSeen(readSeen(electionId, puCode));
		return subscribeCancellationChanged(electionId, puCode, read);
	}, [active, electionId, puCode]);

	const locked = active && isLockedSnapshot(snapshot);
	const row = snapshot.row ?? null;

	// Remember which row locked this unit, so its reinstatement can be announced once — even if
	// the app was closed when the party reinstated it.
	useEffect(() => {
		if (!active || !locked || !row) return;
		if (seen !== String(row.id)) {
			writeSeen(electionId, puCode, row.id);
			setSeen(String(row.id));
		}
	}, [active, locked, row, seen, electionId, puCode]);

	const reinstated =
		active && !locked && row?.state === 'Reinstated' && seen === String(row.id)
			? { at: row.stateChangedAt, reason: row.stateReason }
			: null;

	const acknowledgeReinstated = useCallback(() => {
		if (!electionId || !puCode) return;
		writeSeen(electionId, puCode, null);
		setSeen(null);
	}, [electionId, puCode]);

	return {
		locked,
		pendingSync: active && snapshot.active && snapshot.localOnly,
		reason: snapshot.reason,
		cancelledAt: snapshot.cancelledAt,
		reviewed: row?.reviewStatus === 'Confirmed',
		reinstated,
		acknowledgeReinstated,
	};
};
